/**
 * SlidePreview draws a custom slide on a 16:9 projector stage, scaled to
 * fit its box. It uses the real projector renderer and CSS, so the host
 * sees the same slide the room sees. Font sizes use viewport units, so the
 * scale is close to the TV but not exact.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import { CustomSlideContent } from '../Presentation/PresentationView';
import type { CustomSlide } from '../../utils/slideSequence';
import type { SlideTemplate } from '../../utils/customSlides';
import '../Presentation/PresentationView.css';
import './Slides.css';

const STAGE_W = 1920;
const STAGE_H = 1080;

/** Turn a library template into the shape the projector renders. */
export function templateAsSlide(t: Pick<SlideTemplate, 'kind' | 'title' | 'body' | 'imageUrl' | 'url' | 'caption'>): CustomSlide {
  return {
    id: 'preview',
    kind: t.kind,
    title: t.title,
    body: t.body,
    imageUrl: t.kind === 'IMAGE' ? t.imageUrl : null,
    url: t.kind === 'PROMO' ? t.url : null,
    caption: t.caption,
    anchor: 'START',
    roundNumber: null,
    sortOrder: 0,
    slot: 'START',
    roundIdx: 0,
    fellBack: false,
    schedule: [],
  };
}

const SlidePreview: React.FC<{ slide: CustomSlide; venueName?: string }> = ({ slide, venueName = 'Your venue' }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.25);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const fit = () => setScale(box.clientWidth / STAGE_W || 0.25);
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    ro?.observe(box);
    return () => ro?.disconnect();
  }, []);

  return (
    <div className="slide-preview-box" ref={boxRef} aria-label="Slide preview">
      <div
        className="presentation-view slide-preview-stage"
        style={{ width: STAGE_W, height: STAGE_H, transform: `scale(${scale})` }}
      >
        <div className={`slide slide-custom custom-${slide.kind.toLowerCase()}`}>
          <CustomSlideContent slide={slide} venueName={venueName} />
        </div>
      </div>
    </div>
  );
};

export default SlidePreview;
